/**
 * @author: Richard Scoones @ 1/5/2019
 * @description: Token setting service.
 */
import API from "@irati/utils/services/API"
import {get} from "lodash"
import getSessionId from "../helpers/getSessionId"
import {TokensService, Tokens} from "./interfaces/tokens"

const endpoints = {
  setCredentials: () => "/api/v1/set-credentials",
  getCredentials: () => "/api/v1/get-credentials"
}

const ENCRYPTED_KEY = "accessToken"

let sessionId = ""

class TokenStorage implements TokensService {
  async resume(): Promise<string> {
    if (!API.hasTokens()) {
      const {accessToken} = await fetchTokens()
      if (accessToken) {
        API.setTokens(accessToken)
      }
      sessionId = getSessionId(accessToken)
    }

    return sessionId
  }

  hasResumed(): boolean {
    return true
  }

  set(tokens: Tokens): Promise<boolean> {
    API.setTokens(tokens.accessToken)
    const config = {addCSRF: true, useErrorHandler: false, sentryIgnoreAllErrors: true}
    return API.post(endpoints.setCredentials(), tokens, config).then((res) => {
      setEncryptedToStorage(res.data.encrypted)
      return true
    })
  }

  clear(): Promise<void> {
    try {
      localStorage.removeItem(ENCRYPTED_KEY)
      sessionStorage.removeItem(ENCRYPTED_KEY)
    } catch {
      // storage blocked
    }
    API.clearTokens()
    return clearRemoteTokens().then(() => {})
  }
}

export default new TokenStorage()

function fetchTokens(): Promise<Tokens> {
  const encrypted = getFromStorage(ENCRYPTED_KEY)
  const config = {addCSRF: true, useErrorHandler: false, sentryIgnoreAllErrors: true}
  return API.post(endpoints.getCredentials(), {encrypted}, config).then((res) => {
    const tokens = get(res, "data")
    if (!tokens.accessToken) {
      throw new Error()
    }
    return tokens
  })
}

function clearRemoteTokens(): Promise<boolean> {
  const config = {addCSRF: true, useErrorHandler: false, sentryIgnoreAllErrors: true}
  return API.post(endpoints.setCredentials(), {}, config)
    .catch(() => {})
    .then(() => true)
}

function getFromStorage(key: string) {
  try {
    let item = localStorage.getItem(key)
    if (!item) {
      item = sessionStorage.getItem(key)
    }
    return item
  } catch {
    return null
  }
}

function setEncryptedToStorage(encrypted: string) {
  try {
    localStorage.setItem(ENCRYPTED_KEY, encrypted)
  } catch {
    // storage blocked — token won't persist across sessions
  }
}
